/**
 * Hook to check collection availability for cards when searching
 *
 * IMPORTANT: Availability is calculated BY CARD NAME, not by specific printing.
 * In MTG deck building, 4x Abrade from different sets = 4 copies of Abrade.
 * This matches how deck managers and tournament rules work.
 *
 * Future enhancement: Calculate "in use" counts across active decks
 * to show true availability (owned - in_other_decks).
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { collectionOwnedByScryfallIdAtom, collectionStateAtom } from '@/features/collection/state/collectionAtom';
import { useCardCountInDeck, useDeckCountForCard } from '@/features/decks/hooks/useCrossDeckUsage';
import { activeDeckStateAtom } from '@/features/decks/state/decksAtom';
import { canHaveUnlimitedCopies, getMaxCopiesForFormat } from '@/lib/deckValidation';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import { collectionOwnedByNameAtom, deckUsageByNameAtom } from '@/state/cardAvailability';
import type { DeckFormat } from '@/types/deck';

export interface CardAvailability {
  owned: number;
  inCurrentDeck: number;
  // Future: inOtherDecks tracking requires loading all deck cards
}

/**
 * Build lookups for owned and in-deck quantities aggregated by card name.
 * This hook is used internally by availability hooks.
 */
function useAvailabilityLookups() {
  const collectionState = useAtomValue(collectionStateAtom);
  const activeDeckState = useAtomValue(activeDeckStateAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);

  return useMemo(() => {
    // Build a lookup of owned quantities by card NAME (not scryfall_id)
    // This aggregates all printings of the same card
    const ownedByName = new Map<string, number>();
    if (collectionState.data) {
      for (const collectionCard of collectionState.data) {
        const card = cardsById.get(collectionCard.scryfall_id);
        if (card) {
          const nameLower = card.name_lower;
          const current = ownedByName.get(nameLower) ?? 0;
          ownedByName.set(nameLower, current + collectionCard.quantity);
        }
      }
    }

    // Build a lookup of cards in current deck by card NAME
    // Again, aggregating all printings
    const inCurrentDeckByName = new Map<string, number>();
    if (activeDeckState.cards) {
      for (const deckCard of activeDeckState.cards) {
        const card = cardsById.get(deckCard.scryfall_id);
        if (card) {
          const nameLower = card.name_lower;
          const current = inCurrentDeckByName.get(nameLower) ?? 0;
          inCurrentDeckByName.set(nameLower, current + deckCard.quantity);
        }
      }
    }

    return { ownedByName, inCurrentDeckByName };
  }, [collectionState.data, activeDeckState.cards, cardsById]);
}

/**
 * Hook that returns collection availability data for a set of card names.
 * Availability is aggregated across all printings of each card.
 *
 * @param cardNames - Array of card names (case-insensitive)
 * @returns Map of cardName (lowercase) -> availability info
 */
export function useCollectionAvailability(cardNames: string[]): Map<string, CardAvailability> {
  const { ownedByName, inCurrentDeckByName } = useAvailabilityLookups();

  return useMemo(() => {
    const result = new Map<string, CardAvailability>();

    for (const cardName of cardNames) {
      const nameLower = cardName.toLowerCase();
      result.set(nameLower, {
        owned: ownedByName.get(nameLower) ?? 0,
        inCurrentDeck: inCurrentDeckByName.get(nameLower) ?? 0,
      });
    }

    return result;
  }, [cardNames, ownedByName, inCurrentDeckByName]);
}

/**
 * Simplified hook for checking a single card's availability by name.
 * More efficient for single lookups (like in a card row).
 *
 * @param cardName - The card name (case-insensitive)
 */
export function useCardAvailabilityByName(cardName: string | null): CardAvailability {
  const { ownedByName, inCurrentDeckByName } = useAvailabilityLookups();

  return useMemo(() => {
    if (!cardName) {
      return { owned: 0, inCurrentDeck: 0 };
    }

    const nameLower = cardName.toLowerCase();
    return {
      owned: ownedByName.get(nameLower) ?? 0,
      inCurrentDeck: inCurrentDeckByName.get(nameLower) ?? 0,
    };
  }, [cardName, ownedByName, inCurrentDeckByName]);
}

/**
 * Hook that provides a set of owned card names (lowercased) for quick membership checks.
 * Used for sorting search results to show owned cards first.
 */
export function useOwnedCardNames(): Set<string> {
  const collectionState = useAtomValue(collectionStateAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);

  return useMemo(() => {
    const ownedNames = new Set<string>();
    if (collectionState.data) {
      for (const collectionCard of collectionState.data) {
        const card = cardsById.get(collectionCard.scryfall_id);
        if (card) {
          ownedNames.add(card.name_lower);
        }
      }
    }
    return ownedNames;
  }, [collectionState.data, cardsById]);
}

/**
 * Hook that provides owned scryfall IDs for finding preferred printings.
 * Returns a Set of scryfall IDs that the user owns.
 */
export function useOwnedScryfallIds(): Set<string> {
  const collectionState = useAtomValue(collectionStateAtom);

  return useMemo(() => {
    const ownedIds = new Set<string>();
    if (collectionState.data) {
      for (const card of collectionState.data) {
        ownedIds.add(card.scryfall_id);
      }
    }
    return ownedIds;
  }, [collectionState.data]);
}

/**
 * Hook that returns a map of card name (lowercase) -> owned scryfall ID.
 * Used to substitute search results with owned printings.
 * If user owns multiple printings, returns the first one found.
 */
export function useOwnedPrintingsByName(): Map<string, string> {
  const collectionState = useAtomValue(collectionStateAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);

  return useMemo(() => {
    const ownedByName = new Map<string, string>();
    if (collectionState.data) {
      for (const collectionCard of collectionState.data) {
        const card = cardsById.get(collectionCard.scryfall_id);
        if (card && !ownedByName.has(card.name_lower)) {
          // Store first owned printing for each card name
          ownedByName.set(card.name_lower, collectionCard.scryfall_id);
        }
      }
    }
    return ownedByName;
  }, [collectionState.data, cardsById]);
}

/**
 * Owned printing info for version selection
 */
export interface OwnedPrinting {
  scryfallId: string;
  quantity: number;
}

/**
 * Hook that returns ALL owned printings (with quantities) for a card name.
 * Returns map of scryfallId -> quantity owned.
 * Used for version selector to show all owned printings.
 */
export function useAllOwnedPrintingsForName(cardNameLower: string | null): Map<string, number> {
  const collectionState = useAtomValue(collectionStateAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);

  return useMemo(() => {
    const ownedPrintings = new Map<string, number>();
    if (!cardNameLower || !collectionState.data) return ownedPrintings;

    for (const collectionCard of collectionState.data) {
      const card = cardsById.get(collectionCard.scryfall_id);
      if (card && card.name_lower === cardNameLower) {
        ownedPrintings.set(collectionCard.scryfall_id, collectionCard.quantity);
      }
    }

    return ownedPrintings;
  }, [cardNameLower, collectionState.data, cardsById]);
}

/**
 * Ownership details for a deck card, considering both exact printing and any printing.
 */
export interface DeckCardOwnership {
  /** How many of this exact printing (scryfall_id) the user owns */
  exactOwned: number;
  /** How many of this card (by name, any printing) the user owns total */
  totalOwnedByName: number;
  /** How many copies are needed (deck card quantity) */
  needed: number;
  /** How many are covered by ownership (min of totalOwnedByName and needed) */
  covered: number;
  /** How many are covered by the exact printing */
  exactCovered: number;
  /** How many are covered by other printings (covered - exactCovered) */
  otherVersionsCovered: number;
  /** How many are missing (needed - covered) */
  missing: number;
  /** Whether fully covered (covered >= needed) */
  isFullyCovered: boolean;
  /** Whether any are owned */
  hasAny: boolean;
  /** Copies physically held by other active decks */
  committedElsewhere: number;
  /** Copies earmarked by other brewing decks, which never reserve a physical copy */
  plannedElsewhere: number;
  /** Owned copies left once other active decks take theirs (can be negative) */
  freeForThisDeck: number;
  /** Copies owned but locked into other active decks, on top of any plain ownership shortfall */
  allocationShortfall: number;
}

function sumExcludingDeck(byDeck: Map<string, number> | undefined, excludeDeckId: string | undefined): number {
  if (!byDeck) return 0;
  let total = 0;
  for (const [deckId, quantity] of byDeck) {
    if (deckId === excludeDeckId) continue;
    total += quantity;
  }
  return total;
}

/**
 * Hook that calculates detailed ownership for a deck card.
 * Shows exact printing ownership vs other versions, and whether the owned copies
 * are actually free or already committed to other active decks.
 *
 * @param scryfallId - The scryfall ID of the card in the deck
 * @param cardNameLower - The lowercase card name for name-based lookup
 * @param quantity - How many copies are in the deck
 * @param excludeDeckId - The deck being viewed, so its own copies don't count against it
 */
export function useDeckCardOwnership(
  scryfallId: string | null,
  cardNameLower: string | null,
  quantity: number,
  excludeDeckId?: string,
): DeckCardOwnership {
  const ownedByScryfallId = useAtomValue(collectionOwnedByScryfallIdAtom);
  const ownedByName = useAtomValue(collectionOwnedByNameAtom);
  const usageByName = useAtomValue(deckUsageByNameAtom);

  return useMemo(() => {
    if (!scryfallId || !cardNameLower) {
      return {
        exactOwned: 0,
        totalOwnedByName: 0,
        needed: quantity,
        covered: 0,
        exactCovered: 0,
        otherVersionsCovered: 0,
        missing: quantity,
        isFullyCovered: false,
        hasAny: false,
        committedElsewhere: 0,
        plannedElsewhere: 0,
        freeForThisDeck: 0,
        allocationShortfall: 0,
      };
    }

    const exactOwned = ownedByScryfallId.get(scryfallId) ?? 0;
    const totalOwnedByName = ownedByName.get(cardNameLower) ?? 0;

    // Calculate coverage
    const covered = Math.min(totalOwnedByName, quantity);
    const exactCovered = Math.min(exactOwned, quantity);
    // Other versions cover what exact doesn't, up to the covered amount
    const otherVersionsCovered = Math.max(0, covered - exactCovered);
    const missing = quantity - covered;

    const usage = usageByName.get(cardNameLower);
    const committedElsewhere = sumExcludingDeck(usage?.committed, excludeDeckId);
    const plannedElsewhere = sumExcludingDeck(usage?.planned, excludeDeckId);
    const freeForThisDeck = totalOwnedByName - committedElsewhere;
    const allocationShortfall = Math.max(0, quantity - Math.max(0, freeForThisDeck)) - missing;

    return {
      exactOwned,
      totalOwnedByName,
      needed: quantity,
      covered,
      exactCovered,
      otherVersionsCovered,
      missing,
      isFullyCovered: covered >= quantity,
      hasAny: totalOwnedByName > 0,
      committedElsewhere,
      plannedElsewhere,
      freeForThisDeck,
      allocationShortfall,
    };
  }, [scryfallId, cardNameLower, quantity, excludeDeckId, ownedByScryfallId, ownedByName, usageByName]);
}

/**
 * Comprehensive card addability info for deck building.
 * Combines ownership, cross-deck usage, and format copy limits.
 */
export interface DeckAddability {
  /** Total copies owned across all printings */
  owned: number;
  /** Copies currently in the active deck */
  inCurrentDeck: number;
  /** Copies physically held by other active decks */
  inOtherDecks: number;
  /** Copies earmarked by other brewing decks, which reserve nothing */
  plannedInOtherDecks: number;
  /** Total copies reserved: inCurrentDeck + inOtherDecks */
  totalInUse: number;
  /** Available = owned - totalInUse (can be negative) */
  available: number;
  /** Number of other decks using this card */
  deckCount: number;
  /** Max copies allowed by format rules */
  maxCopies: number;
  /** Whether deck has reached copy limit */
  isAtCopyLimit: boolean;
  /** Whether another copy can be added to deck */
  canAdd: boolean;
}

/**
 * Hook that provides comprehensive addability info for a card in a deck context.
 * Useful for determining if a card can be added and showing availability stats.
 *
 * Can be used from:
 * - Card discovery panel (adding cards to deck)
 * - Collection card details (showing deck usage + add capability)
 * - Any deck building UI
 *
 * @param cardName - Card name (case-insensitive), null to skip
 * @param card - Card data for unlimited copies check (basic lands, etc.)
 * @param deckId - Current deck ID to exclude from "other decks" count
 * @param deckFormat - Deck format for determining max copies
 */
export function useDeckAddability(
  cardName: string | null,
  card: { name: string; oracle_text?: string | null; type_line?: string | null } | null,
  deckId?: string,
  deckFormat?: DeckFormat | null,
): DeckAddability {
  const availability = useCardAvailabilityByName(cardName);
  const usageByName = useAtomValue(deckUsageByNameAtom);
  const deckCount = useDeckCountForCard(cardName?.toLowerCase() ?? null, deckId);
  // Get count in the specific deck (when deckId provided, e.g., from AddToDeckMenu)
  const countInSpecificDeck = useCardCountInDeck(cardName?.toLowerCase() ?? null, deckId);

  return useMemo(() => {
    const { owned } = availability;
    // Use specific deck count when deckId is provided (e.g., AddToDeckMenu context),
    // otherwise fall back to active deck state (deck editor context)
    const inCurrentDeck = deckId ? countInSpecificDeck : availability.inCurrentDeck;
    const usage = cardName ? usageByName.get(cardName.toLowerCase()) : undefined;
    const inOtherDecks = sumExcludingDeck(usage?.committed, deckId);
    const plannedInOtherDecks = sumExcludingDeck(usage?.planned, deckId);
    const totalInUse = inCurrentDeck + inOtherDecks;
    const available = owned - totalInUse;
    const maxCopies = getMaxCopiesForFormat(deckFormat ?? null);

    // Check if at copy limit - cards with unlimited copies are never at limit
    const isAtCopyLimit = card ? !canHaveUnlimitedCopies(card) && inCurrentDeck >= maxCopies : inCurrentDeck >= maxCopies;

    const canAdd = !isAtCopyLimit;

    return {
      owned,
      inCurrentDeck,
      inOtherDecks,
      plannedInOtherDecks,
      totalInUse,
      available,
      deckCount,
      maxCopies,
      isAtCopyLimit,
      canAdd,
    };
  }, [availability, usageByName, cardName, deckCount, card, deckFormat, deckId, countInSpecificDeck]);
}
